import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FallbackImage } from '../../../shared/fallback-image';
import { BannerStore } from '../banner-store';

const AUTOPLAY_MS = 5000;

/**
 * Карусель баннеров на CSS scroll-snap. Блока нет в вёрстке, пока не декодирована первая
 * картинка (ready стора), потом он плавно раскрывается. Автосмена маятником каждые 5 с,
 * замирает под пальцем, курсором и фокусом; кнопка ⏸/▶ — постоянная пауза (WCAG 2.2.2).
 */
@Component({
  selector: 'app-banner-carousel',
  imports: [FallbackImage, TranslatePipe],
  template: `
    @if (banners().length > 0) {
      <div class="reveal" [class.reveal-shown]="ready()">
        <div class="reveal-clip" [attr.inert]="ready() ? null : ''">
          <div class="qm-banners mx-auto max-w-md pt-4 pb-2">
            <div
              #track
              role="region"
              class="qm-banner-track no-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-6 px-6"
              [attr.tabindex]="scrollable() ? 0 : null"
              [attr.aria-label]="'banners.label' | translate"
              [attr.aria-roledescription]="'banners.role' | translate"
              (scroll)="onScroll()"
              (pointerenter)="pointerHeld.set(true)"
              (pointerleave)="pointerHeld.set(false)"
              (focusin)="focusHeld.set(true)"
              (focusout)="focusHeld.set(false)"
            >
              @for (banner of banners(); track banner.id; let i = $index, total = $count) {
                <!-- «2 из 4» без слова «слайд»: его уже произносит aria-roledescription. -->
                <div
                  class="qm-banner w-full shrink-0 snap-start"
                  role="group"
                  [attr.aria-roledescription]="'banners.slideRole' | translate"
                  [attr.aria-label]="'banners.slide' | translate: { n: i + 1, total }"
                >
                  <!-- [loading] — свойство и стоит до [src]: атрибуты Angular ставит после свойств. -->
                  <img
                    appFallbackImage
                    alt=""
                    decoding="async"
                    class="qm-banner-img block aspect-video w-full object-cover"
                    [loading]="i === 0 || (ready() && i <= active() + 1) ? 'eager' : 'lazy'"
                    [src]="banner.imageUrl"
                  />
                </div>
              }
            </div>
            @if (scrollable()) {
              <div class="mt-2 grid grid-cols-[1fr_auto_1fr] items-center px-6">
                <div class="qm-banner-dots col-start-2 flex gap-1.5" aria-hidden="true">
                  @for (banner of banners(); track banner.id; let i = $index) {
                    <span
                      class="qm-banner-dot"
                      [class.qm-banner-dot--active]="i === active()"
                    ></span>
                  }
                </div>
                @if (!reducedMotion()) {
                  <button
                    type="button"
                    class="qm-banner-pause col-start-3 justify-self-end"
                    [attr.aria-label]="(paused() ? 'banners.play' : 'banners.pause') | translate"
                    (click)="togglePause()"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                    >
                      @if (paused()) {
                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                      } @else {
                        <rect x="6" y="4" width="4" height="16"></rect>
                        <rect x="14" y="4" width="4" height="16"></rect>
                      }
                    </svg>
                  </button>
                }
              </div>
            }
          </div>
        </div>
      </div>
    }
  `,
  styles: `
    :host {
      display: block;
    }

    /* Строка грида 0fr→1fr — единственный путь к auto-высоте: контент ниже съезжает плавно.
       min-height: 0 обязателен, иначе строка 0fr не схлопнется. */
    .reveal {
      display: grid;
      grid-template-rows: 0fr;
      transition: grid-template-rows 250ms ease-out;
    }

    .reveal-clip {
      min-height: 0;
      overflow: clip;
      opacity: 0;
      transform: translateY(0.5rem);
      transition:
        opacity 250ms ease-out,
        transform 250ms ease-out;
    }

    .reveal-shown {
      grid-template-rows: 1fr;
    }

    .reveal-shown > .reveal-clip {
      opacity: 1;
      transform: none;
    }

    .qm-banner-track:focus-visible {
      outline: 2px solid var(--qm-focus);
      outline-offset: 2px;
    }

    /* Чистый --qm-divider в default почти не виден на slate-50: подмешиваем ink. */
    .qm-banner-img {
      border-radius: 1.5rem;
      background: color-mix(in srgb, var(--qm-ink) 8%, var(--qm-divider));
    }

    .qm-banner-dot {
      width: 0.375rem;
      height: 0.375rem;
      border-radius: 9999px;
      background: color-mix(in srgb, var(--qm-ink) 25%, transparent);
      transition:
        width 200ms ease-out,
        background-color 200ms ease-out;
    }

    .qm-banner-dot--active {
      width: 1.25rem;
      background: var(--qm-focus);
    }

    .qm-banner-pause {
      display: grid;
      place-items: center;
      width: 2rem;
      height: 2rem;
      border-radius: 9999px;
      color: var(--qm-ink-muted);
      background: transparent;
      cursor: pointer;
    }

    @media (hover: hover) {
      .qm-banner-pause:hover {
        background: color-mix(in srgb, var(--qm-ink) 8%, var(--qm-divider));
      }
    }

    .qm-banner-pause:focus-visible {
      outline: 2px solid var(--qm-focus);
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
      .reveal,
      .reveal-clip,
      .qm-banner-dot {
        transition: none;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerCarousel {
  private store = inject(BannerStore);
  private doc = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef);
  private track = viewChild<ElementRef<HTMLElement>>('track');

  protected readonly banners = this.store.banners;
  protected readonly ready = this.store.ready;
  protected readonly scrollable = computed(() => this.banners().length > 1);

  /** Новая дорожка (язык без баннеров и обратно) стоит в начале и scroll не шлёт — сброс на 0. */
  private readonly activeIndex = linkedSignal({ source: this.track, computation: () => 0 });
  protected readonly active = computed(() =>
    Math.min(this.activeIndex(), Math.max(this.banners().length - 1, 0)),
  );

  protected readonly pointerHeld = signal(false);
  protected readonly focusHeld = signal(false);
  protected readonly paused = signal(false);
  protected readonly reducedMotion = signal(false);
  /** Направление маятника: +1 или −1. */
  private step = 1;

  private readonly autoplays = computed(
    () =>
      this.ready() &&
      this.scrollable() &&
      !this.reducedMotion() &&
      !this.paused() &&
      !this.pointerHeld() &&
      !this.focusHeld(),
  );

  constructor() {
    this.watchReducedMotion();
    // Любая смена условий гасит таймер и заводит новый: после пальца, фокуса или ▶ — полные 5 с.
    effect((onCleanup) => {
      if (!this.autoplays()) return;
      const id = setInterval(() => this.advance(), AUTOPLAY_MS);
      onCleanup(() => clearInterval(id));
    });
  }

  protected togglePause(): void {
    this.paused.update((p) => !p);
  }

  protected onScroll(): void {
    const track = this.track()?.nativeElement;
    if (!track || track.clientWidth === 0) return;
    const max = track.scrollWidth - track.clientWidth;
    // Узкие слайды (graphite) не дотягивают до последней точки привязки: конец дорожки — последний слайд.
    this.activeIndex.set(
      max > 0 && track.scrollLeft >= max - 1
        ? track.children.length - 1
        : Math.round(track.scrollLeft / stride(track)),
    );
  }

  /** Маятником 1→2→3→2→1: разворот — от текущей позиции, ручной свайп счёт не сбивает. */
  private advance(): void {
    // Без zone тик может прийти раньше, чем CD снимет интервал после пальца, фокуса или ⏸;
    // за открытой шторкой не листаем.
    if (!this.autoplays() || this.doc.querySelector('dialog[open]')) return;
    const track = this.track()?.nativeElement;
    const slides = track?.children;
    if (!track || !slides || track.clientWidth === 0 || slides.length < 2) return;
    const current = this.active();
    if (current + this.step >= slides.length || current + this.step < 0) this.step = -this.step;
    const target = slides[current + this.step];
    const first = slides[0];
    // scrollTo, не scrollIntoView: тот прокрутил бы к баннеру всё окно.
    if (target instanceof HTMLElement && first instanceof HTMLElement) {
      track.scrollTo({ left: target.offsetLeft - first.offsetLeft, behavior: 'smooth' });
    }
  }

  /** Живой выключатель, а не снимок: системный тумблер останавливает карусель без перезагрузки. */
  private watchReducedMotion(): void {
    const query = this.doc.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return;
    this.reducedMotion.set(query.matches);
    const onChange = (event: MediaQueryListEvent) => {
      // Кнопка паузы исчезнет вместе с автосменой: фокус с неё — на дорожку, а не на <body>.
      if (event.matches && this.doc.activeElement?.classList.contains('qm-banner-pause')) {
        this.track()?.nativeElement.focus({ preventScroll: true });
      }
      this.reducedMotion.set(event.matches);
    };
    query.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() => query.removeEventListener('change', onChange));
  }
}

/** Шаг из DOM, а не константой: темы меняют ширину слайда и зазор, а qrTextSize — размер rem. */
function stride(track: HTMLElement): number {
  const first = track.children[0];
  const second = track.children[1];
  return first instanceof HTMLElement && second instanceof HTMLElement
    ? second.offsetLeft - first.offsetLeft
    : track.clientWidth;
}
